import {Fragment, useMemo, useState} from 'react'
import {Text, TextInput, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {type BlankFont, blankInputWidth} from '#/lib/blankInputWidth'
import {FeedbackBar} from '#/components/capsules/play/FeedbackBar'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {
  type FBState,
  type SlidePlayProps,
} from '#/components/capsules/play/types'
import {useTextWidths} from '#/components/capsules/play/useTextWidths'
import {
  useFlashBookPlayPalette,
  usePlayContentPadding,
  usePlayPrimaryAction,
  usePlayStackGap,
  usePlaySuppressOwnSurface,
  usePlayTextStyle,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {SLIDE_TYPE_META} from '#/types/capsule'

/**
 * The blank field's own type and room, in one place.
 *
 * Its width is *measured* in this font and grown by this padding and border, so
 * the numbers it is drawn with and the numbers its width is worked out from are
 * the same numbers. The widest of the two borders counts, because on ruled paper
 * the blank keeps only its underline while a boxed theme keeps all four sides —
 * and one width has to serve both.
 */
const BLANK_TYPE = {
  fontSize: 13,
  paddingHorizontal: 8,
  borderWidth: 1.5,
} as const

/** What the field's width has to cover beyond the writing: padding and borders. */
const BLANK_PADDING =
  (BLANK_TYPE.paddingHorizontal + BLANK_TYPE.borderWidth) * 2

export function FillBlanksSlidePlay({
  slide,
  onAnswer,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(20)
  // Inside a book the block owns the surface: on an open/ruled page the exercise
  // is *written on the paper* (each blank a ruled space rather than a filled
  // input box), and on a panel page the block's own panel is already the frame.
  // Only outside a book does this widget draw a box of its own.
  const onPaper = usePlaySuppressOwnSurface()
  // The sentence is text printed on the page, so it is set in the book's own
  // reading type and its line box is one line of the page's ruling.
  const bodyText = usePlayTextStyle('body')
  const lineBox = bodyText.lineHeight ?? 22
  // A whole number of the page's rules between the question and the exercise.
  const stackGap = usePlayStackGap(14)
  // The action: a whole number of rules tall, so everything printed after it
  // is back on the ruling — and, on paper, written rather than filled.
  const action = usePlayPrimaryAction(48)
  const paragraph = slide.fillBlanksParagraph ?? ''
  const answers = slide.fillBlanksAnswers ?? []
  const parts = paragraph.split('___')

  /*
   * The width of a blank is the width of the words it can hold, measured in the
   * font the field prints them in.
   */
  const blankFont: BlankFont = useMemo(
    () => ({
      fontFamily: bodyText.fontFamily,
      fontSize: BLANK_TYPE.fontSize,
    }),
    [bodyText.fontFamily],
  )

  const [vals, setVals] = useState<string[]>(Array(answers.length).fill(''))
  const [checked, setChecked] = useState(false)
  const [retried, setRetried] = useState(false)
  const [showSol, setShowSol] = useState(false)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setVals(Array(answers.length).fill(''))
    setChecked(false)
    setRetried(false)
    setShowSol(false)
  }

  const {widths, probe} = useTextWidths(blankFont, [
    ...answers.flatMap(a => [a.answer, ...(a.alternatives ?? [])]),
    ...vals,
  ])

  const checkA = (a: (typeof answers)[0], v: string) => {
    const u = v.toLowerCase().trim()
    return (
      u === a.answer.toLowerCase().trim() ||
      (a.alternatives ?? []).some(x => x.toLowerCase().trim() === u)
    )
  }

  const results = answers.map((a, i) => checkA(a, vals[i] ?? ''))
  const okCount = results.filter(Boolean).length
  const maxPts = slide.points ?? answers.length
  const pts = answers.length
    ? Math.round((okCount / answers.length) * maxPts)
    : 0
  const allFilled = vals.every(v => v.trim().length > 0)

  const fbState: FBState = showSol
    ? 'solution'
    : okCount === answers.length
      ? 'correct'
      : okCount > 0
        ? 'partial'
        : 'wrong'

  const verify = () => {
    onAnswer({
      isCorrect: okCount === answers.length,
      points: pts,
      maxPoints: maxPts,
      solutionText: answers.map(a => a.answer).join(' · '),
      explanation: slide.explanation,
      userAnswer: vals,
    })
    setChecked(true)
  }

  const retry = () => {
    setVals(Array(answers.length).fill(''))
    setChecked(false)
    setRetried(true)
  }

  return (
    <PlayScroller
      style={{flex: 1}}
      contentContainerStyle={{padding, gap: stackGap, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      {/* The probe the blanks are measured with; see `useTextWidths`. */}
      {probe}
      {!!slide.question && (
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>
      )}

      <View
        style={
          onPaper
            ? {padding: 0}
            : {
                backgroundColor: palette.surfaceAlt,
                borderRadius: 14,
                padding: 16,
                borderWidth: 1,
                borderColor: palette.border,
              }
        }>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 4,
            // On paper the wrapped lines are held together by the ruling
            // itself: any extra row gap would push the second line off the
            // line it is meant to be written on.
            rowGap: onPaper ? 0 : 8,
          }}>
          {parts.map((part, i) => {
            const okF = checked && !showSol && results[i]
            const badF = checked && !showSol && !results[i]
            const bc = showSol
              ? palette.warning
              : okF
                ? palette.positive
                : badF
                  ? palette.negative
                  : primaryColor + '80'
            const a = answers[i]
            // Sized to the word the learner is meant to write — from the
            // answer and its accepted alternatives, widened by whatever they
            // have typed so their own text is never clipped by the box. What the
            // platform measured those words at, in this field's own font.
            const inputW = blankInputWidth(
              [a?.answer, ...(a?.alternatives ?? []), vals[i]],
              {font: blankFont, padding: BLANK_PADDING, measured: widths},
            )
            return (
              <Fragment key={i}>
                {part
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((word, wi) => (
                    <Text
                      key={`p${i}w${wi}`}
                      style={[
                        {color: palette.body, fontSize: 15, lineHeight: 22},
                        bodyText,
                      ]}>
                      {word}
                    </Text>
                  ))}
                {i < answers.length && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 3,
                    }}>
                    <TextInput
                      accessibilityLabel={l`Text input field`}
                      accessibilityHint={undefined}
                      style={{
                        width: inputW,
                        // The field is one line of the page: taller would push
                        // the writing off the rule it sits on.
                        height: onPaper ? lineBox : 32,
                        paddingHorizontal: BLANK_TYPE.paddingHorizontal,
                        fontSize: BLANK_TYPE.fontSize,
                        fontFamily: bodyText.fontFamily,
                        textAlign: 'center',
                        color: showSol ? palette.warning : palette.inputText,
                        // A ruled blank reads as a line to write on; a boxed
                        // blank reads as a form field. The theme chooses.
                        ...(onPaper
                          ? {
                              borderRadius: 0,
                              borderBottomWidth: 2,
                              backgroundColor: 'transparent',
                            }
                          : {
                              borderRadius: 7,
                              borderWidth: BLANK_TYPE.borderWidth,
                              backgroundColor: showSol
                                ? palette.warning + '11'
                                : palette.inputBg,
                            }),
                        borderColor: bc,
                      }}
                      placeholder="…"
                      placeholderTextColor={palette.muted}
                      autoCapitalize="none"
                      autoCorrect={false}
                      value={showSol ? (a?.answer ?? '') : (vals[i] ?? '')}
                      onChangeText={t =>
                        !checked &&
                        !showSol &&
                        setVals(v => {
                          const n = [...v]
                          n[i] = t
                          return n
                        })
                      }
                      editable={!checked && !showSol}
                    />
                    {checked && !showSol && (
                      <Feather
                        name={results[i] ? 'check-circle' : 'x-circle'}
                        size={14}
                        color={results[i] ? palette.positive : palette.negative}
                      />
                    )}
                  </View>
                )}
              </Fragment>
            )
          })}
        </View>
      </View>

      {!checked && !showSol && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={verify}
          disabled={!allFilled}
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              paddingVertical: 14,
              borderRadius: 14,
              backgroundColor: allFilled ? primaryColor : palette.surfaceAlt,
              opacity: allFilled ? 1 : 0.5,
            },
            action.style,
          ]}>
          <Feather
            name="check"
            size={16}
            // The button only reads as "enabled" when it can be pressed, so the
            // label follows the surface it is actually sitting on.
            color={
              action.ink(allFilled) ??
              (allFilled ? palette.onAccent : palette.muted)
            }
          />
          <Text
            style={[
              {
                color:
                  action.ink(allFilled) ??
                  (allFilled ? palette.onAccent : palette.muted),
                fontSize: 16,
                fontWeight: '700',
              },
              action.labelStyle(allFilled),
            ]}>
            <Trans>Check</Trans>
          </Text>
        </TouchableOpacity>
      )}

      {checked && (
        <FeedbackBar
          state={fbState}
          points={pts}
          maxPoints={maxPts}
          explanation={!showSol ? slide.explanation : undefined}
          solutionText={answers.map(a => a.answer).join(' · ')}
          onRetry={!retried ? retry : undefined}
          onShowSolution={
            !showSol
              ? () => {
                  setShowSol(true)
                  setChecked(true)
                }
              : undefined
          }
        />
      )}
    </PlayScroller>
  )
}
